import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Search, Download, Calendar, FileText, BarChart, TrendingUp, Zap, CreditCard, Share2, ExternalLink } from "lucide-react";
import PraiseLogo from "@/components/PraiseLogo";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import Navbar from "@/components/layout/Navbar";
import ReferralWidget from "@/components/referral/ReferralWidget";
import PreferencesForm from "@/components/settings/PreferencesForm";
import ManageSubscription from "@/components/checkout/ManageSubscription";
import { format } from "date-fns";
import { toast } from "sonner";

interface Testimonial {
  id: string;
  client_name: string;
  brand_name: string;
  created_at: string;
  context: string | null;
}

interface Transformation {
  id: string;
  testimonial_id: string;
  output_type: string;
  content: string;
  created_at: string;
}

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, profile, isLoading } = useAuth();
  const [searchTerm, setSearchTerm] = useState("");
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [transformations, setTransformations] = useState<Transformation[]>([]);
  const [isLoadingData, setIsLoadingData] = useState(true);

  useEffect(() => {
    if (!isLoading && !user) {
      navigate("/auth");
    }
  }, [user, isLoading, navigate]);

  useEffect(() => {
    if (user) {
      fetchData();
    }
  }, [user]);

  const fetchData = async () => {
    try {
      const [testimonialsRes, transformationsRes] = await Promise.all([
        supabase
          .from("testimonials")
          .select("*")
          .order("created_at", { ascending: false }),
        supabase
          .from("transformations")
          .select("*")
          .order("created_at", { ascending: false }),
      ]);

      if (testimonialsRes.data) setTestimonials(testimonialsRes.data);
      if (transformationsRes.data) setTransformations(transformationsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setIsLoadingData(false);
    }
  };

  const getTransformationsForTestimonial = (testimonialId: string) => {
    return transformations.filter((t) => t.testimonial_id === testimonialId);
  };

  const filteredTestimonials = testimonials.filter(
    (t) =>
      t.client_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.brand_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const stats = {
    totalTransformations: testimonials.length,
    creditsUsed: profile?.credits_used_total || 0,
    creditsRemaining: profile?.credits_remaining || 0,
    totalOutputs: transformations.length,
  };

  if (isLoading || !user) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-brand-50 via-background to-accent/20">
      <Navbar />

      <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8 gap-4">
          <div>
            <h1 className="text-3xl font-bold text-foreground mb-2">Dashboard</h1>
            <p className="text-muted-foreground">
              Manage your testimonial transformations and track usage
            </p>
          </div>
          <Link to="/agent">
            <Button className="gradient-brand text-white shadow-brand">
              <Plus className="mr-2 h-4 w-4" />
              New Transformation
            </Button>
          </Link>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card className="border-brand-100 hover:shadow-lg transition-shadow">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <PraiseLogo size={16} />
                Transformations
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-foreground">{stats.totalTransformations}</div>
              <p className="text-xs text-muted-foreground mt-1">Total testimonials processed</p>
            </CardContent>
          </Card>
          <Card className="border-brand-100 hover:shadow-lg transition-shadow">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <FileText className="h-4 w-4 text-brand-500" />
                Content Pieces
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-foreground">{stats.totalOutputs}</div>
              <p className="text-xs text-muted-foreground mt-1">Marketing assets created</p>
            </CardContent>
          </Card>
          <Card className="border-brand-100 hover:shadow-lg transition-shadow">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <Zap className="h-4 w-4 text-brand-500" />
                Credits Used
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-foreground">{stats.creditsUsed}</div>
              <p className="text-xs text-muted-foreground mt-1">Lifetime usage</p>
            </CardContent>
          </Card>
          <Card className="border-brand-100 hover:shadow-lg transition-shadow">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <CreditCard className="h-4 w-4 text-brand-500" />
                Credits Remaining
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-brand-600">{stats.creditsRemaining}</div>
              <p className="text-xs text-muted-foreground mt-1">
                <Link to="/pricing" className="text-brand-600 hover:underline">
                  Upgrade for more
                </Link>
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Main Content */}
        <Tabs defaultValue="transformations" className="space-y-6">
          <TabsList className="bg-muted/50">
            <TabsTrigger value="transformations">Transformations</TabsTrigger>
            <TabsTrigger value="analytics">Analytics</TabsTrigger>
            <TabsTrigger value="settings">Settings</TabsTrigger>
          </TabsList>

          <TabsContent value="transformations" className="space-y-6">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by client or brand..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            {isLoadingData ? (
              <div className="text-center py-12">
                <div className="animate-spin h-8 w-8 border-4 border-brand-500 border-t-transparent rounded-full mx-auto" />
                <p className="text-muted-foreground mt-4">Loading your transformations...</p>
              </div>
            ) : filteredTestimonials.length === 0 ? (
              <Card className="border-dashed border-2 border-brand-200">
                <CardContent className="py-12 text-center">
                  <PraiseLogo size={48} className="mx-auto mb-4 opacity-30" />
                  <h3 className="text-lg font-semibold text-foreground mb-2">
                    No transformations yet
                  </h3>
                  <p className="text-muted-foreground mb-4">
                    Start by transforming your first testimonial into marketing gold
                  </p>
                  <Link to="/agent">
                    <Button className="gradient-brand text-white">
                      <Plus className="mr-2 h-4 w-4" />
                      Create Your First Transformation
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {filteredTestimonials.map((testimonial) => {
                  const formats = getTransformationsForTestimonial(testimonial.id);
                  return (
                    <Card key={testimonial.id} className="border-border hover:shadow-md transition-shadow">
                      <CardContent className="p-6">
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <h3 className="font-semibold text-lg text-foreground">
                                {testimonial.client_name} - {testimonial.brand_name}
                              </h3>
                              <Badge variant="default" className="bg-success/10 text-success border-success/20">
                                Completed
                              </Badge>
                            </div>
                            <div className="flex items-center gap-4 text-sm text-muted-foreground mb-3">
                              <span className="flex items-center">
                                <Calendar className="mr-1 h-4 w-4" />
                                {format(new Date(testimonial.created_at), "MMM d, yyyy")}
                              </span>
                              <span className="flex items-center">
                                <FileText className="mr-1 h-4 w-4" />
                                {formats.length} outputs
                              </span>
                            </div>
                            <div className="flex flex-wrap gap-2">
                              {formats.map((f) => (
                                <Badge key={f.id} variant="outline" className="text-xs capitalize">
                                  {f.output_type.replace(/_/g, " ")}
                                </Badge>
                              ))}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                const shareUrl = `${window.location.origin}/share/${testimonial.id}`;
                                navigator.clipboard.writeText(shareUrl);
                                toast.success("Share link copied!");
                              }}
                            >
                              <Share2 className="mr-2 h-4 w-4" />
                              Share
                            </Button>
                            <Link to={`/share/${testimonial.id}`}>
                              <Button variant="outline" size="sm">
                                <ExternalLink className="mr-2 h-4 w-4" />
                                View
                              </Button>
                            </Link>
                            <Button size="sm" className="bg-gradient-to-r from-brand-600 to-brand-500">
                              <Download className="mr-2 h-4 w-4" />
                              Export
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>

          <TabsContent value="analytics" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <BarChart className="mr-2 h-5 w-5 text-brand-500" />
                  Usage Analytics
                </CardTitle>
                <CardDescription>
                  Track your transformation patterns and credit usage over time
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-64 flex items-center justify-center text-muted-foreground border-2 border-dashed border-border rounded-lg">
                  <div className="text-center">
                    <TrendingUp className="h-12 w-12 mx-auto mb-4 text-brand-300" />
                    <p>Analytics charts coming soon</p>
                    <p className="text-sm">Track your ROI and content performance</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="settings" className="space-y-6">
            {/* Content Preferences */}
            <PreferencesForm />
            
            <div className="grid lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Account Settings</CardTitle>
                  <CardDescription>
                    Manage your account preferences and subscription
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between p-4 border rounded-lg">
                    <div>
                      <h4 className="font-medium">Current Plan</h4>
                      <p className="text-sm text-muted-foreground capitalize">
                        {profile?.plan || "Free"} Plan - {profile?.credits_remaining} credits remaining
                      </p>
                    </div>
                    <Link to="/pricing">
                      <Button variant="outline">Upgrade Plan</Button>
                    </Link>
                  </div>
                  <div className="flex items-center justify-between p-4 border rounded-lg">
                    <div>
                      <h4 className="font-medium">Billing & Subscription</h4>
                      <p className="text-sm text-muted-foreground">
                        Manage your subscription and payment methods
                      </p>
                    </div>
                    <ManageSubscription />
                  </div>
                    <div className="flex items-center justify-between p-4 border rounded-lg">
                      <div>
                        <h4 className="font-medium">API Access</h4>
                        <p className="text-sm text-muted-foreground">
                          Integrate Praise with your tools (Scale+ plans)
                        </p>
                      </div>
                    <Button variant="outline" disabled={profile?.plan === "free"}>
                      View API Docs
                    </Button>
                  </div>
                </CardContent>
              </Card>

              <ReferralWidget />
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Dashboard;
